/**
 * bewirtungBooking — PURE Buchungs-Logik für Bewirtungsbelege (§4 Abs. 5 S. 1 Nr. 2 EStG, M13b-3).
 *
 * Diese Datei trägt die ❌ VETO-Leitplanke des Bewirtungs-Features (architecture-m13b.md §6.3):
 * die PHYSISCHE Trennung der beiden Steuer-Achsen.
 *
 *   • Vorsteuer (USt / VAT):  IMMER 100 % abziehbar. Wird von KEINER Funktion dieser Datei
 *                             berührt. Es existiert KEIN Rechenpfad, der irgendein `vat*`-Feld
 *                             mit `0.7 ×` o. ä. kürzt.
 *   • Ertragsteuer (EStG):    70 % (`geschaeftlich_70`) bzw. 100 % (`betrieblich_100`). Lebt
 *                             ausschließlich in `BewirtungInfo.incomeTaxDeductiblePercent` und
 *                             ist REINE DATEV-/Audit-Information — fließt in keine VAT-Berechnung.
 *
 * Trinkgeld läuft als separate 0 %-Zeile mit (`vatOwedCents:0`, `vatDeductibleCents:0`), sodass
 * die Invariante `grossCents === Σ netCents + Σ vatOwedCents + roundingDiff` erhalten bleibt
 * (das Trinkgeld erhöht netCents UND grossCents um denselben Betrag, vat bleibt unberührt).
 *
 * Cent-Disziplin: alle Geldbeträge sind ganzzahlige EUR-Cents. Float-`tipCents` ist ein
 * fail-closed-Fehler (throw), da `validateCentFields` dies nicht prüft (expenseTypes.ts §tipCents).
 *
 * KEINE firebase/React-Imports → standalone unit-testbar. Nur Typ-Imports.
 *
 * @module bewirtungBooking — M13b Bewirtungsbeleg
 */

import type { BewirtungInfo, ExpenseVatBreakdownEntry } from '../types/expenseTypes';

// ─── Konstanten ─────────────────────────────────────────────────────────────

/**
 * §33 UStDV-Schwelle für die Pflichtangabe „Name des Bewirtenden", in EUR-Cents.
 * Oberhalb (`grossCents > 25_000`) ist `hostName` Pflicht (250,00 €).
 */
const HOST_NAME_THRESHOLD_CENTS = 25_000;

// ─── Typen ──────────────────────────────────────────────────────────────────

/**
 * Gültige Bewirtungsart (ohne den UI-„noch nicht gewählt"-Zustand `''`).
 * `geschaeftlich_70` → 70 % ertragsteuerlich; `betrieblich_100` → 100 %.
 */
export type BewirtungArt = 'geschaeftlich_70' | 'betrieblich_100';

/**
 * Schmaler Eingabe-Typ für die pure Bewirtungs-Logik. Wird vom `ReceiptScanModal`-State
 * befüllt (M13b-4). Minimal gehalten — nur die für Vollständigkeit + Build nötigen Felder.
 */
export interface BewirtungForm {
  /**
   * Bewirtungsart. `''` = im Modal noch keine Auswahl getroffen (fail-closed:
   * gilt als unvollständig, kann nicht gebucht werden).
   */
  art: '' | BewirtungArt;

  /** Bewirtete Personen (roh, inkl. evtl. leerer Einträge — getrimmt geprüft/gebaut). */
  participants: string[];

  /** Anlass der Bewirtung (roh). */
  occasion: string;

  /** Name des Bewirtenden (roh, leer = nicht angegeben). Pflicht ab > 250 €. */
  hostName: string;

  /**
   * Bestätigtes Trinkgeld in ganzzahligen EUR-Cents. Muss nicht-negativ und ganzzahlig sein
   * (Float → fail-closed throw in `buildBewirtungInfo`/`appendTipLine`/`adjustTotalsForTip`).
   */
  tipCents: number;

  /**
   * Optionaler handschriftlicher Gesamtbetrag in EUR-Cents (vom Server-Kandidat / Beleg).
   * Ist er gesetzt, muss `tipCents === handwrittenTotalCents − printedGrossCents` gelten (±0).
   * `null`/`undefined` → diese Gleichheitsprüfung entfällt (nur Nicht-Negativität/Ganzzahligkeit).
   */
  handwrittenTotalCents?: number | null;

  /**
   * `true` wenn Teilnehmer/Anlass/Trinkgeld handschriftlich auf dem Beleg ergänzt sind
   * (GoBD-Nachweis). Wird 1:1 in `BewirtungInfo.selfDeclaredOnReceipt` übernommen.
   */
  selfDeclaredOnReceipt: boolean;
}

// ─── Interne Helfer ─────────────────────────────────────────────────────────

/**
 * Fail-closed-Wächter: stellt sicher, dass ein zu buchender Trinkgeld-Betrag ganzzahlig ist.
 * Float-Cents würden die Cent-Invariante (`netCents`/`grossCents` ganzzahlig) still verletzen.
 *
 * @param tipCents Zu prüfender Trinkgeld-Betrag in Cents.
 * @throws {Error} wenn `tipCents` keine ganze Zahl ist.
 */
function assertIntegerTip(tipCents: number): void {
  if (!Number.isInteger(tipCents)) {
    throw new Error(
      `bewirtungBooking: tipCents muss ganzzahlig (Integer-Cents) sein, war ${tipCents}`
    );
  }
}

/**
 * Trinkgeld-Plausibilität (§4 architecture-m13b.md): nicht-negative ganze Zahl; bei bekanntem
 * handschriftlichem Gesamtbetrag zusätzlich exakte Gleichheit `tipCents === handwritten − gedruckt`.
 *
 * @param form Bewirtungs-Eingabe.
 * @param printedGrossCents Gedrucktes Brutto (vor Trinkgeld-Anpassung), in Cents.
 * @returns `true` wenn das Trinkgeld plausibel ist.
 */
function isTipPlausible(form: BewirtungForm, printedGrossCents: number): boolean {
  const { tipCents, handwrittenTotalCents } = form;
  if (!Number.isInteger(tipCents) || tipCents < 0) return false;
  if (handwrittenTotalCents === null || handwrittenTotalCents === undefined) return true;
  return tipCents === handwrittenTotalCents - printedGrossCents;
}

// ─── Exportierte pure Funktionen ────────────────────────────────────────────

/**
 * Prüft, ob die §4-Abs.5-Pflichtangaben einer Bewirtung vollständig sind (Buchen-Gate, §6.4).
 *
 * Vollständig genau dann, wenn ALLE gelten:
 *   1. `art !== ''` (Bewirtungsart gewählt),
 *   2. mindestens ein nicht-leerer (getrimmter) Teilnehmer,
 *   3. nicht-leerer (getrimmter) Anlass,
 *   4. `grossCents <= 25_000` ODER nicht-leerer `hostName` (§33-UStDV-Schwelle 250 €),
 *   5. Trinkgeld-Plausibilität (nicht-negative ganze Zahl; ggf. == handwritten − gedruckt).
 *
 * @param form Aktueller Bewirtungs-Eingabezustand.
 * @param grossCents Gedrucktes Brutto des Belegs (vor Trinkgeld), in Cents.
 * @returns `true` wenn buchbar (vollständig), sonst `false`.
 */
export function isBewirtungComplete(form: BewirtungForm, grossCents: number): boolean {
  if (form.art === '') return false;
  const hasParticipant = form.participants.some((p) => p.trim() !== '');
  if (!hasParticipant) return false;
  if (form.occasion.trim() === '') return false;
  if (grossCents > HOST_NAME_THRESHOLD_CENTS && form.hostName.trim() === '') return false;
  if (!isTipPlausible(form, grossCents)) return false;
  return true;
}

/**
 * Leitet den ertragsteuerlichen Abzugsprozentsatz aus der Bewirtungsart ab.
 * REINE DATEV-/Audit-Information — fließt in KEINE VAT-Berechnung (❌ VETO-Leitplanke §6.3).
 *
 * @param art Gültige Bewirtungsart (`''` ist hier kein erlaubter Input — Typ enggeführt).
 * @returns `70` für `geschaeftlich_70`, `100` für `betrieblich_100`.
 * @throws {Error} fail-closed bei unerwartetem Wert (sollte durch den Typ ausgeschlossen sein).
 */
export function deriveIncomeTaxPercent(art: BewirtungArt): 70 | 100 {
  switch (art) {
    case 'geschaeftlich_70':
      return 70;
    case 'betrieblich_100':
      return 100;
    default: {
      const exhaustive: never = art;
      throw new Error(
        `deriveIncomeTaxPercent: ungültige Bewirtungsart '${String(exhaustive)}' (fail-closed)`
      );
    }
  }
}

/**
 * Baut das `BewirtungInfo`-Objekt aus dem Modal-Formular.
 *
 * Garantien (fail-closed):
 *   • `art === ''` → throw (Bewirtungsart fehlt).
 *   • `tipCents` keine ganze Zahl → throw (Cent-Invariante).
 *   • `tipCents < 0` → throw (kein negatives Trinkgeld buchbar).
 *   • Teilnehmer werden getrimmt, leere Einträge entfernt.
 *   • `hostName` getrimmt; leer → `null`.
 *
 * Schreibt KEIN `vat*`-Feld — die Vorsteuer bleibt unberührt (❌ VETO-Leitplanke §6.3).
 *
 * @param form Bewirtungs-Eingabe (sollte vorab `isBewirtungComplete` erfüllen).
 * @returns Das fertige `BewirtungInfo`-Objekt.
 * @throws {Error} bei fehlender Art oder nicht-ganzzahligem/negativem `tipCents`.
 */
export function buildBewirtungInfo(form: BewirtungForm): BewirtungInfo {
  if (form.art === '') {
    throw new Error('buildBewirtungInfo: Bewirtungsart fehlt (fail-closed)');
  }
  assertIntegerTip(form.tipCents);
  if (form.tipCents < 0) {
    throw new Error(
      `buildBewirtungInfo: tipCents darf nicht negativ sein, war ${form.tipCents} (fail-closed)`
    );
  }

  const participants = form.participants.map((p) => p.trim()).filter((p) => p !== '');
  const trimmedHost = form.hostName.trim();

  return {
    art: form.art,
    incomeTaxDeductiblePercent: deriveIncomeTaxPercent(form.art),
    participants,
    occasion: form.occasion.trim(),
    hostName: trimmedHost === '' ? null : trimmedHost,
    tipCents: form.tipCents,
    selfDeclaredOnReceipt: form.selfDeclaredOnReceipt,
  };
}

/**
 * Hängt — nur bei `tipCents > 0` — eine separate 0 %-Trinkgeld-Zeile an die effektive
 * VAT-Breakdown an. Die Zeile ist steuerneutral: `vatOwedCents === 0 && vatDeductibleCents === 0`.
 * Sie wird NIE in die gedruckten 7/19-Zeilen gemischt (❌ VETO-Leitplanke §6.3).
 *
 * Immutabel: das Eingabe-Array wird nicht mutiert, es wird stets eine neue Kopie zurückgegeben.
 *
 * @param effectiveBreakdown Effektive VAT-Breakdown (gedruckte 7/19-Zeilen).
 * @param tipCents Bestätigtes Trinkgeld in Cents.
 * @returns Neue Array-Kopie; bei `tipCents <= 0` unverändert, sonst mit angehängter 0 %-Zeile.
 * @throws {Error} wenn `tipCents > 0` und nicht ganzzahlig (Cent-Invariante).
 */
export function appendTipLine(
  effectiveBreakdown: ExpenseVatBreakdownEntry[],
  tipCents: number
): ExpenseVatBreakdownEntry[] {
  const copy = [...effectiveBreakdown];
  if (tipCents <= 0) return copy;
  assertIntegerTip(tipCents);
  copy.push({
    categoryCode: 'E',
    taxRate: 0,
    netCents: tipCents,
    vatOwedCents: 0,
    vatDeductibleCents: 0,
  });
  return copy;
}

/**
 * Passt Netto- und Brutto-Summe um das Trinkgeld an (`+tipCents` auf BEIDE).
 *
 * `vatOwedCents`/`vatDeductibleCents` werden bewusst NICHT berührt und sind nicht Teil der
 * Rückgabe — das Trinkgeld läuft als 0 %-Netto mit, sodass die Invariante
 * `grossCents === Σ netCents + Σ vatOwedCents + roundingDiff` erhalten bleibt (❌ VETO §6.3).
 *
 * @param netCents Netto-Summe vor Trinkgeld, in Cents.
 * @param grossCents Brutto-Summe vor Trinkgeld, in Cents.
 * @param tipCents Bestätigtes Trinkgeld in Cents.
 * @returns `{ netCents, grossCents }` jeweils `+tipCents`; bei `tipCents <= 0` unverändert.
 * @throws {Error} wenn `tipCents > 0` und nicht ganzzahlig (Cent-Invariante).
 */
export function adjustTotalsForTip(
  netCents: number,
  grossCents: number,
  tipCents: number
): { netCents: number; grossCents: number } {
  if (tipCents <= 0) return { netCents, grossCents };
  assertIntegerTip(tipCents);
  return { netCents: netCents + tipCents, grossCents: grossCents + tipCents };
}
